import { useMemo } from 'react';

import { isSkilledNursingUnit } from 'src/constants/CareTypes';
import {
    AssistLevel,
    CareInfoDisplayItem,
    CareInfoDisplayOrder,
    getCareInfoDisplay,
    isAllCareInfoUnknown,
    NoCareInfoDisplay,
} from 'src/constants/ResidentCareInfoValues';
import useFacilityFeatures from 'src/hooks/useFacilityFeatures';
import useUserPermissions from 'src/hooks/useUserPermissions';
import {
    Resident,
    ResidentCareInfoItem,
    useGetResidentCareInfoQuery,
} from 'src/store/sageAdminApi';

const isIndependent = ({ key, value }: ResidentCareInfoItem) =>
    key === 'ASSIST_LEVEL' && value === AssistLevel.INDEPENDENT;

export default function useResidentCareInfoDisplays(
    resident?: Resident,
    { showIndependent = false }: { showIndependent?: boolean } = {}
) {
    const { canSeePHI } = useUserPermissions();
    const { hasResidentCareInfo } = useFacilityFeatures();
    const residentId = resident?.id;
    const isEnabled = canSeePHI && hasResidentCareInfo && isSkilledNursingUnit(resident?.unit);

    const queryResult = useGetResidentCareInfoQuery(
        { residentId: residentId! },
        { skip: !residentId || !isEnabled }
    );
    // Not `data`: that keeps the previous resident's care info while a new residentId loads.
    const { currentData } = queryResult;

    const careInfoDisplays = useMemo((): CareInfoDisplayItem[] => {
        if (!isEnabled) {
            return [];
        }
        const careInfo = currentData?.careInfo ?? [];
        if (isAllCareInfoUnknown(careInfo)) {
            return [{ ...NoCareInfoDisplay, key: 'no-care-info' }];
        }
        return CareInfoDisplayOrder.flatMap((key) => careInfo.filter((item) => item.key === key))
            .filter((item) => showIndependent || !isIndependent(item))
            .flatMap((item) => {
                const display = getCareInfoDisplay(item);
                return display
                    ? [{ ...display, key: item.rid ?? `${item.key}-${item.value}` }]
                    : [];
            });
    }, [currentData, isEnabled, showIndependent]);

    return { ...queryResult, careInfoDisplays };
}
